// place-search renderer — the `PlacesCard` a2ui root.
// One surface holds a map + N place details: markers and detail cards derive from the
// same `places[]` list, so tapping a marker highlights its detail card with no re-query.
// Kept in its own module because it needs React hooks (SDK loading, render-time clock)
// that can't live inside A2UISurfaceRenderer's plain render helpers.
//
// Integrity note: all place data (coords, name, address, hours, …) is filled by the
// backend `_run` from the find_places source — the FE only *displays* it. We render
// text as plain text (React auto-escapes; no dangerouslySetInnerHTML) and URL-encode
// names in deep-links (output-boundary hygiene).
import React, { useEffect, useRef, useState } from 'react';
import { MapPin, Phone, Clock, Navigation, Siren } from 'lucide-react';
import { computeOpenNow, type Interval } from '../utils/openNow';
import type { A2UIComponent } from './ChatComponents';

/* eslint-disable @typescript-eslint/no-explicit-any -- map SDKs (naver/google) are loaded
   at runtime via injected <script> and have no bundled type packages here. */

type Provider = 'naver' | 'google';

interface PlaceItem {
  placeId: string;
  lat: number;
  lng: number;
  name: string;
  category?: string;
  address?: string;
  tel?: string;
  hours?: Interval[] | null;
  tz_id: string;
  emergency?: boolean;
}

// Provider attribution is code-forced (never LLM-authored), rendered on every PlacesCard.
// KR/naver source is the public-data holder under KOGL; "네이버 검색 제공" is false and removed.
const ATTRIBUTION: Record<Provider, string> = {
  naver: '국립중앙의료원 제공',
  google: 'Google Maps',
};

const toProvider = (v: unknown): Provider => (v === 'google' ? 'google' : 'naver');

// Deep-links follow the canvas provider — a naver-canvas place must not bounce the user
// to Google Maps (provider mismatch). google data stays on Google Maps (ToS §3.2.3(e)).
// ⚠️ naver 지도는 클라이언트 앱이라 URL 은 200 이어도 좌표/경로 파싱은 브라우저에서
// 최종 확인 필요(형식은 map.naver.com 현행 경로 기준). 좌표 순서 주의: naver=lng,lat.
const directionsLink = (lat: number, lng: number, name: string, provider: Provider): string =>
  provider === 'naver'
    ? `https://map.naver.com/p/directions/-/${lng},${lat},${encodeURIComponent(name)}/-/transit`
    : `https://www.google.com/maps/dir/?api=1&destination=${lat}%2C${lng}&destination_name=${encodeURIComponent(name)}`;

const Attribution: React.FC<{ provider: Provider }> = ({ provider }) => (
  <div className="px-3 py-1.5 text-[11px] text-slate-400 border-t border-slate-100">
    {ATTRIBUTION[provider]}
  </div>
);

// ── Map ──────────────────────────────────────────────────────────────────────

// Load an external SDK <script> once; resolve when ready, reject on error. Reuses an
// already-injected tag (keyed by id) so remounting doesn't append duplicates.
const loadScript = (src: string, id: string): Promise<void> =>
  new Promise((resolve, reject) => {
    const existing = document.getElementById(id) as HTMLScriptElement | null;
    if (existing) {
      if (existing.dataset.loaded === 'true') resolve();
      else {
        existing.addEventListener('load', () => resolve());
        existing.addEventListener('error', () => reject(new Error('map sdk load error')));
      }
      return;
    }
    const s = document.createElement('script');
    s.id = id;
    s.src = src;
    s.async = true;
    s.addEventListener('load', () => {
      s.dataset.loaded = 'true';
      resolve();
    });
    s.addEventListener('error', () => reject(new Error('map sdk load error')));
    document.head.appendChild(s);
  });

// Live map — markers from `places`, camera from server center/zoom. Tapping a marker
// calls `onSelect(placeId)` so the matching detail card highlights. Renders nothing
// (returns null via the parent's `mapFailed`) when the SDK fails; the detail list below
// is always shown, so the surface stays useful without a live map.
const LiveMap: React.FC<{
  provider: Provider;
  apiKey: string;
  center: { lat: number; lng: number };
  zoom: number;
  places: PlaceItem[];
  onSelect: (placeId: string) => void;
  onFail: () => void;
}> = ({ provider, apiKey, center, zoom, places, onSelect, onFail }) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    const src =
      provider === 'google'
        ? `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}`
        : `https://oapi.map.naver.com/openapi/v3/maps.js?ncpKeyId=${encodeURIComponent(apiKey)}`;
    const id = `map-sdk-${provider}`;

    loadScript(src, id)
      .then(() => {
        if (cancelled || !ref.current) return;
        // Camera uses the server-provided center/zoom (computed from the same bbox FE
        // fitBounds would use) — no user-location marker (no GPS).
        if (provider === 'google') {
          const g = (window as any).google;
          const map = new g.maps.Map(ref.current, { center, zoom });
          for (const p of places) {
            const mk = new g.maps.Marker({ position: { lat: p.lat, lng: p.lng }, map, title: p.name });
            mk.addListener('click', () => onSelect(p.placeId));
          }
        } else {
          const n = (window as any).naver;
          const map = new n.maps.Map(ref.current, {
            center: new n.maps.LatLng(center.lat, center.lng),
            zoom,
          });
          for (const p of places) {
            const mk = new n.maps.Marker({ position: new n.maps.LatLng(p.lat, p.lng), map, title: p.name });
            n.maps.Event.addListener(mk, 'click', () => onSelect(p.placeId));
          }
        }
      })
      .catch(() => {
        if (!cancelled) onFail();
      });
    return () => {
      cancelled = true;
    };
  }, [provider, apiKey, center, zoom, places, onSelect, onFail]);

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      <div ref={ref} className="h-64 w-full bg-slate-100" />
    </div>
  );
};

// ── Place detail ──────────────────────────────────────────────────────────────

const openBadge = (status: ReturnType<typeof computeOpenNow>): React.ReactNode => {
  if (status.openNow === null) {
    return <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">정보 없음</span>;
  }
  if (status.openNow && status.closingSoon) {
    return <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">곧 마감</span>;
  }
  if (status.openNow) {
    return <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">영업중</span>;
  }
  return <span className="rounded-full bg-slate-200 px-2 py-0.5 text-xs font-medium text-slate-600">영업종료</span>;
};

const PlaceDetail: React.FC<{
  place: PlaceItem;
  provider: Provider;
  highlighted: boolean;
  detailRef?: (el: HTMLDivElement | null) => void;
}> = ({ place, provider, highlighted, detailRef }) => {
  // Render-time computation (N1): the badge reflects *now*, recomputed on every render.
  // country_code isn't on the wire — infer holiday-awareness from provider (KR public
  // data = holiday-aware; Google = not). Mirrors compute_open_now's holiday_aware flag.
  const hours = Array.isArray(place.hours) ? place.hours : null;
  const status = computeOpenNow(hours, place.tz_id || 'Asia/Seoul', new Date(), {
    holidayAware: provider === 'naver',
    countryCode: provider === 'naver' ? 'KR' : null,
  });

  return (
    <div
      ref={detailRef}
      className={`rounded-xl border bg-white p-4 space-y-2 transition-shadow ${
        highlighted ? 'border-blue-400 ring-2 ring-blue-200' : 'border-slate-200'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-0.5">
          <div className="text-base font-bold text-slate-800">{place.name}</div>
          {place.category && <div className="text-xs text-slate-500">{place.category}</div>}
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {place.emergency === true && (
            <span className="flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
              <Siren size={11} /> 응급실
            </span>
          )}
          {openBadge(status)}
        </div>
      </div>

      {place.address && (
        <div className="flex items-start gap-2 text-sm text-slate-600">
          <MapPin size={14} className="mt-0.5 shrink-0 text-slate-400" />
          <span>{place.address}</span>
        </div>
      )}
      {place.tel && (
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <Phone size={14} className="shrink-0 text-slate-400" />
          <a href={`tel:${place.tel}`} className="text-blue-600 hover:text-blue-800">
            {place.tel}
          </a>
        </div>
      )}
      {status.todayHours && (
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <Clock size={14} className="shrink-0 text-slate-400" />
          <span>오늘 {status.todayHours}</span>
        </div>
      )}

      <a
        href={directionsLink(place.lat, place.lng, place.name, provider)}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
      >
        <Navigation size={12} /> 길찾기
      </a>
    </div>
  );
};

// ── PlacesCard ────────────────────────────────────────────────────────────────

export const PlacesCardSurface: React.FC<{ comp: A2UIComponent }> = ({ comp }) => {
  const provider = toProvider(comp.provider);
  const places: PlaceItem[] = Array.isArray(comp.places) ? comp.places : [];
  const center = comp.center && typeof comp.center.lat === 'number' ? comp.center : { lat: 0, lng: 0 };
  const zoom = typeof comp.zoom === 'number' ? comp.zoom : 14;
  const apiKey =
    provider === 'google'
      ? import.meta.env.VITE_GOOGLE_MAPS_BROWSER_KEY
      : import.meta.env.VITE_NCP_MAPS_CLIENT_ID;

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [mapFailed, setMapFailed] = useState(false);
  const detailRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Marker tap → highlight the matching detail + scroll it into view (no re-query;
  // the detail is already in `places`).
  const handleSelect = React.useCallback((placeId: string) => {
    setSelectedId(placeId);
    detailRefs.current[placeId]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, []);

  if (places.length === 0) return null;

  // N=1 → collapse the map, show the single detail card only.
  const showMap = places.length > 1 && !!apiKey && !mapFailed;

  if (!apiKey && import.meta.env.DEV && places.length > 1) {
    const varName = provider === 'google' ? 'VITE_GOOGLE_MAPS_BROWSER_KEY' : 'VITE_NCP_MAPS_CLIENT_ID';
    // eslint-disable-next-line no-console
    console.warn(`[PlacesCard] ${varName} absent at build — showing detail list only, no live ${provider} map.`);
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      <div className="p-3 space-y-3">
        {showMap && (
          <LiveMap
            provider={provider}
            apiKey={apiKey as string}
            center={center}
            zoom={zoom}
            places={places}
            onSelect={handleSelect}
            onFail={() => setMapFailed(true)}
          />
        )}
        {mapFailed && places.length > 1 && (
          <div className="text-xs text-amber-600">지도를 불러오지 못했습니다 — 목록으로 표시합니다.</div>
        )}
        <div className="space-y-2">
          {places.map((p) => (
            <PlaceDetail
              key={p.placeId}
              place={p}
              provider={provider}
              highlighted={selectedId === p.placeId}
              detailRef={(el) => {
                detailRefs.current[p.placeId] = el;
              }}
            />
          ))}
        </div>
      </div>
      <Attribution provider={provider} />
    </div>
  );
};

/* eslint-enable @typescript-eslint/no-explicit-any */
